import { PlanId, SubscriptionStatus } from "../enums.js";
export const PLAN_LIMITS = {
    [PlanId.free]: {
        maxActiveLoads: 10,
        maxDevices: 1,
        ocrPerMonth: null,
        routesPerMonth: null,
        exports: false,
        fleet: false,
    },
    [PlanId.solo]: {
        maxActiveLoads: null,
        maxDevices: null,
        ocrPerMonth: 300,
        routesPerMonth: 200,
        exports: true,
        fleet: false,
    },
    [PlanId.fleet]: {
        maxActiveLoads: null,
        maxDevices: null,
        ocrPerMonth: 300,
        routesPerMonth: 200,
        exports: true,
        fleet: true,
    },
};
export const SOLO_PRICE_MONTH_CENTS = 1500;
export const SOLO_PRICE_YEAR_CENTS = 15000;
export const FLEET_SEAT_MONTH_CENTS = 1200;
export const FLEET_MIN_SEATS = 2;
export const TRIAL_DAYS = 14;
export const GRACE_DAYS = 7;
/**
 * effectivePlan:
 * - trialing and trialEndsAt > now -> solo writable
 * - active -> plan writable
 * - past_due and graceEndsAt > now -> writable with banner
 * - past_due after grace, canceled, incomplete -> readOnly
 * - none -> free writable
 *
 * @param nowMs - inject clock (pure; callers pass Date.now() outside)
 */
export function effectivePlan(sub, nowMs) {
    const status = sub.status;
    if (status === SubscriptionStatus.trialing) {
        const trialEnd = sub.trialEndsAt ? Date.parse(sub.trialEndsAt) : 0;
        if (trialEnd > nowMs) {
            return {
                planId: PlanId.solo,
                writable: true,
                readOnly: false,
                showPastDueBanner: false,
                limits: scaleFleetLimits(PLAN_LIMITS.solo, 1),
            };
        }
        // Trial expired -> free writable
        return {
            planId: PlanId.free,
            writable: true,
            readOnly: false,
            showPastDueBanner: false,
            limits: PLAN_LIMITS.free,
        };
    }
    if (status === SubscriptionStatus.active) {
        const limits = scaleFleetLimits(PLAN_LIMITS[sub.planId] ?? PLAN_LIMITS.solo, sub.seats);
        return {
            planId: sub.planId,
            writable: true,
            readOnly: false,
            showPastDueBanner: false,
            limits,
        };
    }
    if (status === SubscriptionStatus.past_due) {
        const graceEnd = sub.graceEndsAt ? Date.parse(sub.graceEndsAt) : 0;
        if (graceEnd > nowMs) {
            const limits = scaleFleetLimits(PLAN_LIMITS[sub.planId] ?? PLAN_LIMITS.solo, sub.seats);
            return {
                planId: sub.planId,
                writable: true,
                readOnly: false,
                showPastDueBanner: true,
                limits,
            };
        }
        return {
            planId: sub.planId,
            writable: false,
            readOnly: true,
            showPastDueBanner: true,
            limits: scaleFleetLimits(PLAN_LIMITS[sub.planId] ?? PLAN_LIMITS.solo, sub.seats),
        };
    }
    if (status === SubscriptionStatus.canceled ||
        status === SubscriptionStatus.incomplete) {
        return {
            planId: sub.planId,
            writable: false,
            readOnly: true,
            showPastDueBanner: false,
            limits: PLAN_LIMITS.free,
        };
    }
    // none
    return {
        planId: PlanId.free,
        writable: true,
        readOnly: false,
        showPastDueBanner: false,
        limits: PLAN_LIMITS.free,
    };
}
/** Fleet: per-month limits × seats. */
function scaleFleetLimits(base, seats) {
    if (!base.fleet && seats <= 1)
        return base;
    const s = Math.max(1, seats);
    return {
        ...base,
        ocrPerMonth: base.ocrPerMonth != null ? base.ocrPerMonth * s : null,
        routesPerMonth: base.routesPerMonth != null ? base.routesPerMonth * s : null,
    };
}
/** Writes allowed when readOnly: sign-in, export, billing, sync/pull only. */
export function isWriteAllowedWhenReadOnly(action) {
    return (action === "sign_in" ||
        action === "export" ||
        action === "billing" ||
        action === "sync_pull");
}
//# sourceMappingURL=index.js.map